import { PopupEventWrapper } from 'components';
import { ContentSlot, DialogTextArea } from 'components/PopupEvent/styled';
import { EditEventType } from 'constants/sidebars';
import { NotificationType } from 'pages/CommunitySettings/NotificationsTab/types';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMedia } from 'react-use';
import { useEventDetails } from 'sidebars/EventDetails/hooks';
import {
  getCurrentCommunity,
  getCurrentUserInCommunity,
} from 'store/selectors/currentCommunity';
import { getCurrentGroup } from 'store/selectors/currentGroup';
import { breakpoints } from 'theme';
import { Event } from 'types';
import { isNotifyPopupDisabled } from 'utils';

interface EventDeleteConfirmPopupsProps {
  event: Event;
  isOpen: boolean;
  onClose: () => void;
}

const EventDeleteConfirmPopups: FC<EventDeleteConfirmPopupsProps> = ({
  event,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const isDownMd = useMedia(breakpoints.downMd);
  const community = useSelector(getCurrentCommunity);
  const group = useSelector(getCurrentGroup);
  const currentUser = useSelector(getCurrentUserInCommunity);
  const [notifyPopup, setNotifyPopup] = useState<{
    isOpen: boolean;
    type?: EditEventType;
    notifyText?: string;
  }>({
    isOpen: false,
  });

  const isOverallEvent = !!event?.communityId && !event?.groupId;

  const { onDeleteEvents } = useEventDetails({
    groupId: group?.id || event?.groupId,
    communityId: community?.id || event?.communityId,
    eventId: event?.id,
    event,
    isOverallEvent,
  });

  if (!event || !currentUser || !community) {
    return null;
  }

  const onDeleteButtonClick = (type: EditEventType) => {
    const ignoreNotifyPopup = isNotifyPopupDisabled(
      NotificationType.EVENT_DELETE,
      event,
      community.notifications,
    );

    onClose();

    if (ignoreNotifyPopup) {
      return onDeleteEvents(type, false);
    }

    setNotifyPopup({ isOpen: true, type });
  };

  const deleteSelectionOptions = [
    {
      value: EditEventType.SINGLE_EVENT,
      label: t('event.eventOperations.eventOnly'),
      handleConfirm: () => onDeleteButtonClick(EditEventType.SINGLE_EVENT),
    },
    {
      value: EditEventType.SINGLE_AND_FOLLOW,
      label: t('event.eventOperations.eventAndFollowUp'),
      handleConfirm: () => onDeleteButtonClick(EditEventType.SINGLE_AND_FOLLOW),
    },
    {
      value: EditEventType.FULL_EVENT,
      label: t('event.eventOperations.all'),
      handleConfirm: () => onDeleteButtonClick(EditEventType.FULL_EVENT),
    },
  ];

  const deletePopupButtons = [
    {
      popupButtonText: t('common.ok'),
      handleConfirm: () => onDeleteButtonClick(EditEventType.SINGLE_EVENT),
    },
    {
      popupButtonText: t('common.cancel'),
      handleConfirm: () => onClose(),
    },
  ];

  const notifyPopupButtons = [
    {
      popupButtonText: t('communitySettings.notificationPopupBack'),
      variant: 'text',
      handleConfirm: () => setNotifyPopup({ isOpen: false }),
    },
    {
      popupButtonText: t('communitySettings.notificationPopupDoNotNotify'),
      variant: 'text',
      handleConfirm: () => {
        onDeleteEvents(notifyPopup.type, false);
        setNotifyPopup({ isOpen: false });
      },
    },
    {
      popupButtonText: t('communitySettings.notificationPopupNotify'),
      handleConfirm: () => {
        onDeleteEvents(notifyPopup.type, true, notifyPopup.notifyText);
        setNotifyPopup({ isOpen: false });
      },
    },
  ];

  return (
    <>
      {event.eventSeriesId ? (
        <PopupEventWrapper
          isOpen={isOpen}
          onPopupClose={onClose}
          popupMessageText={t('event.eventOperations.deleteText')}
          selectionOptions={deleteSelectionOptions}
          defaultSelectionValue={EditEventType.SINGLE_EVENT}
          darkBackground={true}
        />
      ) : (
        <PopupEventWrapper
          isOpen={isOpen}
          onPopupClose={onClose}
          popupMessageText={t('groupSettings.deleteGroupHeader')}
          popupButtons={deletePopupButtons}
          darkBackground={true}
        />
      )}

      {notifyPopup.isOpen && (
        <PopupEventWrapper
          innerComponent={
            <ContentSlot>
              <DialogTextArea
                value={notifyPopup.notifyText}
                placeholder={t(
                  'communitySettings.notificationPopupMessageLabel',
                )}
                rows={4}
                onChange={(e) =>
                  setNotifyPopup({
                    ...notifyPopup,
                    notifyText: e.target.value,
                  })
                }
              />
            </ContentSlot>
          }
          isOpen={true}
          popupMessageText={t('communitySettings.notificationPopupHeader')}
          popupButtons={notifyPopupButtons}
          enhancedLayout
          stackFooterActions={isDownMd}
          popupStyles={{
            width: isDownMd ? '94%' : '520px',
            maxWidth: '520px',
          }}
          darkBackground={true}
          onPopupClose={() => setNotifyPopup({ isOpen: false })}
        />
      )}
    </>
  );
};

export default EventDeleteConfirmPopups;
